import dejavuBold from '../assets/fonts/DejaVuSans-Bold.ttf';
import dejavuRegular from '../assets/fonts/DejaVuSans.ttf';
import urbanistBold from '../assets/fonts/Urbanist-Bold.ttf';
import urbanistItalic from '../assets/fonts/Urbanist-Italic.ttf';
import urbanistRegular from '../assets/fonts/Urbanist-Regular.ttf';

export const PDF_PRIMARY_FONT = 'Urbanist';
export const PDF_FALLBACK_FONT = 'DejaVuSans';

type FontStyle = 'normal' | 'bold' | 'italics' | 'bolditalics';
type GlyphCoverage = (codePoint: number) => boolean;

type LoadedFont = {
  base64?: string;
  coverage: GlyphCoverage;
};

type FontAssets = Record<FontStyle, LoadedFont>;

export type PdfTextRun = {
  text: string;
  font: string;
};

export type PdfFontFallbackContext = {
  registerJsPdfFonts: (pdf: any) => void;
  registerPdfMakeFonts: (pdfMake: any) => void;
  splitText: (text: string, fontName?: string, fontStyle?: string) => PdfTextRun[];
  splitTextToSize: (pdf: any, text: string, maxWidth: number) => string[];
  transformPdfMakeContent: (content: unknown) => unknown;
  installJsPdfFallback: (pdf: any) => () => void;
};

type CoverageOverrides = Partial<Record<'urbanistRegular' | 'urbanistBold' | 'urbanistItalic' | 'dejavuRegular' | 'dejavuBold', GlyphCoverage>>;

const FONT_FILES = {
  urbanistRegular: 'Urbanist-Regular.ttf',
  urbanistBold: 'Urbanist-Bold.ttf',
  urbanistItalic: 'Urbanist-Italic.ttf',
  dejavuRegular: 'DejaVuSans.ttf',
  dejavuBold: 'DejaVuSans-Bold.ttf',
} as const;

let fontFallbackPromise: Promise<PdfFontFallbackContext> | undefined;

const readUInt16 = (bytes: Uint8Array, offset: number): number =>
  (bytes[offset] << 8) | bytes[offset + 1];

const readUInt32 = (bytes: Uint8Array, offset: number): number =>
  ((bytes[offset] * 0x1000000) + (bytes[offset + 1] << 16) + (bytes[offset + 2] << 8) + bytes[offset + 3]) >>> 0;

const hasRange = (bytes: Uint8Array, offset: number, length: number): boolean =>
  offset >= 0 && length >= 0 && offset + length <= bytes.length;

const parseFormat12Coverage = (bytes: Uint8Array, offset: number): GlyphCoverage | null => {
  if (!hasRange(bytes, offset, 16) || readUInt16(bytes, offset) !== 12) return null;
  const length = readUInt32(bytes, offset + 4);
  const groupCount = readUInt32(bytes, offset + 12);
  if (!hasRange(bytes, offset, length) || !hasRange(bytes, offset + 16, groupCount * 12)) return null;

  const groups: Array<[number, number]> = [];
  for (let index = 0; index < groupCount; index += 1) {
    const groupOffset = offset + 16 + index * 12;
    groups.push([readUInt32(bytes, groupOffset), readUInt32(bytes, groupOffset + 4)]);
  }

  return (codePoint: number): boolean => {
    let low = 0;
    let high = groups.length - 1;
    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      const [start, end] = groups[middle];
      if (codePoint < start) high = middle - 1;
      else if (codePoint > end) low = middle + 1;
      else return true;
    }
    return false;
  };
};

const parseFormat4Coverage = (bytes: Uint8Array, offset: number): GlyphCoverage | null => {
  if (!hasRange(bytes, offset, 16) || readUInt16(bytes, offset) !== 4) return null;
  const length = readUInt16(bytes, offset + 2);
  const segCount = readUInt16(bytes, offset + 6) / 2;
  if (!hasRange(bytes, offset, length) || segCount === 0) return null;

  const endCodesOffset = offset + 14;
  const startCodesOffset = endCodesOffset + segCount * 2 + 2;
  const idDeltaOffset = startCodesOffset + segCount * 2;
  const idRangeOffsetOffset = idDeltaOffset + segCount * 2;
  if (!hasRange(bytes, idRangeOffsetOffset, segCount * 2)) return null;

  return (codePoint: number): boolean => {
    if (codePoint < 0 || codePoint > 0xffff) return false;
    for (let index = 0; index < segCount; index += 1) {
      const end = readUInt16(bytes, endCodesOffset + index * 2);
      if (codePoint > end) continue;
      const start = readUInt16(bytes, startCodesOffset + index * 2);
      if (codePoint < start) return false;

      const delta = readUInt16(bytes, idDeltaOffset + index * 2);
      const rangeOffset = readUInt16(bytes, idRangeOffsetOffset + index * 2);
      if (rangeOffset === 0) return ((codePoint + delta) & 0xffff) !== 0;

      const glyphOffset = idRangeOffsetOffset + index * 2 + rangeOffset + (codePoint - start) * 2;
      if (!hasRange(bytes, glyphOffset, 2)) return false;
      const glyphId = readUInt16(bytes, glyphOffset);
      return glyphId !== 0 && ((glyphId + delta) & 0xffff) !== 0;
    }
    return false;
  };
};

const parseCmapCoverage = (bytes: Uint8Array): GlyphCoverage => {
  if (!hasRange(bytes, 0, 12)) return () => false;
  const tableCount = readUInt16(bytes, 4);
  let cmapOffset = -1;
  for (let index = 0; index < tableCount; index += 1) {
    const tableOffset = 12 + index * 16;
    if (!hasRange(bytes, tableOffset, 16)) break;
    const tag = String.fromCharCode(bytes[tableOffset], bytes[tableOffset + 1], bytes[tableOffset + 2], bytes[tableOffset + 3]);
    if (tag === 'cmap') {
      cmapOffset = readUInt32(bytes, tableOffset + 8);
      break;
    }
  }
  if (!hasRange(bytes, cmapOffset, 4)) return () => false;

  const subtableCount = readUInt16(bytes, cmapOffset + 2);
  const coverage: GlyphCoverage[] = [];
  for (let index = 0; index < subtableCount; index += 1) {
    const encodingOffset = cmapOffset + 4 + index * 8;
    if (!hasRange(bytes, encodingOffset, 8)) break;
    const subtableOffset = cmapOffset + readUInt32(bytes, encodingOffset + 4);
    if (!hasRange(bytes, subtableOffset, 2)) continue;
    const format = readUInt16(bytes, subtableOffset);
    const parsed = format === 12
      ? parseFormat12Coverage(bytes, subtableOffset)
      : format === 4
        ? parseFormat4Coverage(bytes, subtableOffset)
        : null;
    if (parsed) coverage.push(parsed);
  }
  return (codePoint: number) => coverage.some((covers) => covers(codePoint));
};

const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(offset, Math.min(offset + chunkSize, bytes.length))));
  }
  return window.btoa(binary);
};

const loadFont = async (url: string): Promise<LoadedFont> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`PDF font could not be loaded: ${url}`);
  const buffer = await response.arrayBuffer();
  return { base64: arrayBufferToBase64(buffer), coverage: parseCmapCoverage(new Uint8Array(buffer)) };
};

const normalizeStyle = (fontStyle?: string): FontStyle => {
  const style = (fontStyle || 'normal').toLowerCase();
  if (style.includes('bold') && (style.includes('italic') || style.includes('oblique'))) return 'bolditalics';
  if (style.includes('bold')) return 'bold';
  if (style.includes('italic') || style.includes('oblique')) return 'italics';
  return 'normal';
};

const isPrimaryFont = (fontName?: string): boolean => !fontName || fontName === PDF_PRIMARY_FONT || fontName === 'fontInter';

const runTextWidth = (pdf: any, text: string, splitText: PdfFontFallbackContext['splitText'], getTextWidth: (text: string) => number): number => {
  const previousFont = pdf.getFont?.();
  try {
    let activeFontName = previousFont?.fontName;
    return splitText(text, previousFont?.fontName, previousFont?.fontStyle).reduce((width, run) => {
      if (run.font !== activeFontName) {
        pdf.setFont(run.font, previousFont?.fontStyle?.includes('bold') ? 'bold' : 'normal');
        activeFontName = run.font;
      }
      return width + getTextWidth(run.text);
    }, 0);
  } finally {
    if (previousFont) pdf.setFont(previousFont.fontName, previousFont.fontStyle);
  }
};

const createContext = (urbanistFonts: FontAssets, dejavuFonts: FontAssets): PdfFontFallbackContext => {
  const splitText = (text: string, fontName = PDF_PRIMARY_FONT, fontStyle = 'normal'): PdfTextRun[] => {
    if (!isPrimaryFont(fontName) || !text) return [{ text, font: fontName || PDF_PRIMARY_FONT }];
    const style = normalizeStyle(fontStyle);
    const primary = urbanistFonts[style];
    const fallback = dejavuFonts[style];
    const runs: PdfTextRun[] = [];
    let currentFont: string | undefined;
    let currentText = '';
    const append = (font: string, value: string) => {
      if (currentFont !== undefined && currentFont !== font) {
        runs.push({ text: currentText, font: currentFont });
        currentText = '';
      }
      currentFont = font;
      currentText += value;
    };

    for (const character of Array.from(text)) {
      const codePoint = character.codePointAt(0)!;
      if (primary.coverage(codePoint)) append(PDF_PRIMARY_FONT, character);
      else if (fallback.coverage(codePoint)) append(PDF_FALLBACK_FONT, character);
      else append(PDF_PRIMARY_FONT, '?');
    }
    if (currentFont !== undefined) runs.push({ text: currentText, font: currentFont });
    return runs;
  };

  const splitWithMeasure = (text: string, maxWidth: number, measure: (value: string) => number): string[] => {
    if (!text) return [''];
    const lines: string[] = [];
    for (const paragraph of text.replace(/\r\n?/g, '\n').split('\n')) {
      if (!paragraph) {
        lines.push('');
        continue;
      }
      let line = '';
      for (const word of paragraph.trim().split(/\s+/)) {
        const candidate = line ? `${line} ${word}` : word;
        if (line && measure(candidate) > maxWidth) {
          lines.push(line);
          line = word;
        } else {
          line = candidate;
        }
        while (line && measure(line) > maxWidth) {
          const characters = Array.from(line);
          let fitting = '';
          let overflow = '';
          for (const character of characters) {
            if (!overflow && measure(fitting + character) <= maxWidth) fitting += character;
            else overflow += character;
          }
          if (!fitting) {
            lines.push(characters[0]);
            line = characters.slice(1).join('');
          } else {
            lines.push(fitting);
            line = overflow;
          }
        }
      }
      if (line) lines.push(line);
    }
    return lines.length ? lines : [''];
  };

  const splitTextToSize = (pdf: any, text: string, maxWidth: number): string[] => {
    const getTextWidth = pdf.getTextWidth.bind(pdf);
    return splitWithMeasure(text, maxWidth, (value) => runTextWidth(pdf, value, splitText, getTextWidth));
  };

  const registerJsPdfFonts = (pdf: any) => {
    const add = (fileName: string, family: string, style: string, font: LoadedFont) => {
      if (!font.base64) throw new Error('PDF font binary is unavailable.');
      pdf.addFileToVFS(fileName, font.base64);
      pdf.addFont(fileName, family, style);
    };
    add(FONT_FILES.urbanistRegular, PDF_PRIMARY_FONT, 'normal', urbanistFonts.normal);
    add(FONT_FILES.urbanistBold, PDF_PRIMARY_FONT, 'bold', urbanistFonts.bold);
    add(FONT_FILES.urbanistItalic, PDF_PRIMARY_FONT, 'italic', urbanistFonts.italics);
    add(FONT_FILES.urbanistBold, PDF_PRIMARY_FONT, 'bolditalic', urbanistFonts.bolditalics);
    // pre-prod's legacy proposal generator still selects this family name. Registering the
    // same Urbanist binaries keeps it compatible while emitted text runs use Urbanist.
    add(FONT_FILES.urbanistRegular, 'fontInter', 'normal', urbanistFonts.normal);
    add(FONT_FILES.urbanistBold, 'fontInter', 'bold', urbanistFonts.bold);
    add(FONT_FILES.dejavuRegular, PDF_FALLBACK_FONT, 'normal', dejavuFonts.normal);
    add(FONT_FILES.dejavuBold, PDF_FALLBACK_FONT, 'bold', dejavuFonts.bold);
    add(FONT_FILES.dejavuRegular, PDF_FALLBACK_FONT, 'italic', dejavuFonts.italics);
    add(FONT_FILES.dejavuBold, PDF_FALLBACK_FONT, 'bolditalic', dejavuFonts.bolditalics);
  };

  const registerPdfMakeFonts = (pdfMake: any) => {
    if (!pdfMake.vfs) pdfMake.vfs = {};
    const add = (fileName: string, font: LoadedFont) => {
      if (!font.base64) throw new Error('PDF font binary is unavailable.');
      pdfMake.vfs[fileName] = font.base64;
    };
    add(FONT_FILES.urbanistRegular, urbanistFonts.normal);
    add(FONT_FILES.urbanistBold, urbanistFonts.bold);
    add(FONT_FILES.urbanistItalic, urbanistFonts.italics);
    add(FONT_FILES.dejavuRegular, dejavuFonts.normal);
    add(FONT_FILES.dejavuBold, dejavuFonts.bold);
    pdfMake.fonts = {
      ...(pdfMake.fonts || {}),
      [PDF_PRIMARY_FONT]: {
        normal: FONT_FILES.urbanistRegular,
        bold: FONT_FILES.urbanistBold,
        italics: FONT_FILES.urbanistItalic,
        bolditalics: FONT_FILES.urbanistBold,
      },
      [PDF_FALLBACK_FONT]: {
        normal: FONT_FILES.dejavuRegular,
        bold: FONT_FILES.dejavuBold,
        italics: FONT_FILES.dejavuRegular,
        bolditalics: FONT_FILES.dejavuBold,
      },
    };
  };

  const transformPdfMakeContent = (content: unknown): unknown => {
    const transformText = (text: string, fontName: string, fontStyle: string): string | Array<{ text: string; font?: string }> => {
      const runs = splitText(text, fontName, fontStyle);
      return runs.some((run) => run.font === PDF_FALLBACK_FONT)
        ? runs.map((run) => run.font === PDF_FALLBACK_FONT ? { text: run.text, font: PDF_FALLBACK_FONT } : { text: run.text })
        : text;
    };
    const transform = (value: unknown, inheritedFont = PDF_PRIMARY_FONT, inheritedBold = false, inheritedItalics = false): unknown => {
      if (typeof value === 'string') {
        const inheritedStyle = inheritedBold && inheritedItalics ? 'bolditalics' : inheritedBold ? 'bold' : inheritedItalics ? 'italics' : 'normal';
        const transformed = transformText(value, inheritedFont, inheritedStyle);
        return typeof transformed === 'string' ? transformed : { text: transformed };
      }
      if (Array.isArray(value)) return value.map((entry) => transform(entry, inheritedFont, inheritedBold, inheritedItalics));
      if (!value || typeof value !== 'object') return value;

      const node = value as Record<string, any>;
      const fontName = typeof node.font === 'string' ? node.font : inheritedFont;
      const nodeBold = node.bold === undefined ? inheritedBold : Boolean(node.bold);
      const nodeItalics = node.italics === undefined ? inheritedItalics : Boolean(node.italics);
      const nodeStyle = nodeBold && nodeItalics ? 'bolditalics' : nodeBold ? 'bold' : nodeItalics ? 'italics' : 'normal';
      const result: Record<string, any> = { ...node };
      if (typeof node.text === 'string') result.text = transformText(node.text, fontName, nodeStyle);
      else if (Array.isArray(node.text)) result.text = node.text.map((entry: unknown) => transform(entry, fontName, nodeBold, nodeItalics));
      for (const key of ['stack', 'columns', 'ul', 'ol', 'header', 'footer']) {
        if (result[key] !== undefined) result[key] = transform(result[key], fontName, nodeBold, nodeItalics);
      }
      if (result.table && typeof result.table === 'object' && Array.isArray(result.table.body)) {
        result.table = { ...result.table, body: result.table.body.map((row: unknown) => transform(row, fontName, nodeBold, nodeItalics)) };
      }
      return result;
    };
    return transform(content);
  };

  const installJsPdfFallback = (pdf: any): (() => void) => {
    const originalText = pdf.text;
    const originalGetTextWidth = pdf.getTextWidth;
    const originalGetStringUnitWidth = pdf.getStringUnitWidth;
    const originalSplitTextToSize = pdf.splitTextToSize;

    const measure = (value: string): number => runTextWidth(pdf, value, splitText, originalGetTextWidth.bind(pdf));
    const drawLine = (value: string, x: number, y: number, options?: any, transform?: any) => {
      const previousFont = pdf.getFont?.();
      const runs = splitText(value, previousFont?.fontName, previousFont?.fontStyle);
      const alignment = options?.align;
      let drawX = x;
      const width = runs.reduce((total: number, run: PdfTextRun) => total + measure(run.text), 0);
      if (alignment === 'center') drawX -= width / 2;
      if (alignment === 'right') drawX -= width;
      try {
        let activeFontName = previousFont?.fontName;
        for (const run of runs) {
          if (run.font !== activeFontName) {
            pdf.setFont(run.font, previousFont?.fontStyle?.includes('bold') ? 'bold' : 'normal');
            activeFontName = run.font;
          }
          originalText.call(pdf, run.text, drawX, y, { ...options, align: 'left' }, transform);
          drawX += originalGetTextWidth.call(pdf, run.text);
        }
      } finally {
        if (previousFont) pdf.setFont(previousFont.fontName, previousFont.fontStyle);
      }
    };

    pdf.text = (...args: any[]) => {
      const legacyOrder = typeof args[0] === 'number' && typeof args[1] === 'number';
      const value = legacyOrder ? args[2] : args[0];
      const x = legacyOrder ? args[0] : args[1];
      const y = legacyOrder ? args[1] : args[2];
      const options = legacyOrder ? args[3] : args[3];
      const transform = legacyOrder ? args[4] : args[4];
      const lineHeight = pdf.getFontSize() * pdf.getLineHeightFactor() / pdf.internal.scaleFactor;
      const lines = Array.isArray(value) ? value : String(value).split('\n');
      lines.forEach((line: string, index: number) => drawLine(line, x, y + index * lineHeight, options, transform));
      return pdf;
    };
    pdf.getTextWidth = (value: string) => measure(String(value));
    pdf.getStringUnitWidth = (value: string) => measure(String(value)) * pdf.internal.scaleFactor / pdf.getFontSize();
    pdf.splitTextToSize = (value: string, maxWidth: number) => splitWithMeasure(String(value), maxWidth, measure);

    return () => {
      pdf.text = originalText;
      pdf.getTextWidth = originalGetTextWidth;
      pdf.getStringUnitWidth = originalGetStringUnitWidth;
      pdf.splitTextToSize = originalSplitTextToSize;
    };
  };

  return { registerJsPdfFonts, registerPdfMakeFonts, splitText, splitTextToSize, transformPdfMakeContent, installJsPdfFallback };
};

export const createPdfFontFallbackForTesting = (overrides: CoverageOverrides): PdfFontFallbackContext => {
  const noGlyphs = () => false;
  const urbanist: FontAssets = {
    normal: { coverage: overrides.urbanistRegular || noGlyphs },
    bold: { coverage: overrides.urbanistBold || overrides.urbanistRegular || noGlyphs },
    italics: { coverage: overrides.urbanistItalic || overrides.urbanistRegular || noGlyphs },
    bolditalics: { coverage: overrides.urbanistBold || overrides.urbanistRegular || noGlyphs },
  };
  const dejavu: FontAssets = {
    normal: { coverage: overrides.dejavuRegular || noGlyphs },
    bold: { coverage: overrides.dejavuBold || overrides.dejavuRegular || noGlyphs },
    italics: { coverage: overrides.dejavuRegular || noGlyphs },
    bolditalics: { coverage: overrides.dejavuBold || overrides.dejavuRegular || noGlyphs },
  };
  return createContext(urbanist, dejavu);
};

export const loadPdfFontFallback = (): Promise<PdfFontFallbackContext> => {
  if (!fontFallbackPromise) {
    fontFallbackPromise = Promise.all([
      loadFont(urbanistRegular),
      loadFont(urbanistBold),
      loadFont(urbanistItalic),
      loadFont(dejavuRegular),
      loadFont(dejavuBold),
    ]).then(([regular, bold, italic, fallbackRegular, fallbackBold]) => createContext(
      { normal: regular, bold, italics: italic, bolditalics: bold },
      { normal: fallbackRegular, bold: fallbackBold, italics: fallbackRegular, bolditalics: fallbackBold },
    ));
  }
  return fontFallbackPromise;
};
